import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import { SxProps } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import {
    mdiChevronRight,
    mdiDotsVertical,
    mdiLaptopAccount,
    mdiMicrosoftTeams,
} from '@mdi/js';

import { IOfferEventDateFragment } from 'graphql/types';
import { CalendarMenu } from 'common/components/CalendarMenu';
import { getCalendarDescription } from 'offer/utils/calendar';
import { Link } from 'common/components/Link';
import { getOfferEventTimes } from 'offer/utils/dates';
import { IconButton } from 'common/components/IconButton';
import { Icon } from 'common/components/Icon';
import { useFeature } from 'common/hooks/useFeature';
import {
    ListItem,
    ListItemMedia,
    ListItemMediaDate,
    ListItemSecondaryAction,
    ListItemText,
} from 'common/components/ListItem';
import { OfferEventDateLocation } from 'offer/components/OfferEventDetail/OfferEventDateLocation';

const webinarIconWrapperStyle: SxProps = {
    position: 'absolute',
    top: 0,
    left: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '36px',
    height: '36px',
    backgroundColor: 'primary.main',
    color: 'primary.contrastText',
    borderTopLeftRadius: 7,
    borderBottomRightRadius: 7,
};

interface IProps extends React.ComponentProps<typeof ListItem> {
    date: IOfferEventDateFragment;
    offerTitle: string;
    canAccessData?: boolean;
    descriptionAfterSubscription?: string;
}

export const OfferEventDateListItem = ({
    date,
    offerTitle,
    canAccessData,
    descriptionAfterSubscription,
    ...other
}: IProps) => {
    const [translate] = useTranslation();
    const { showChat } = useFeature(undefined, true);

    const isPast = new Date(date.endDate) < new Date();

    const description = getCalendarDescription(
        window.location.href,
        descriptionAfterSubscription,
        date.webinarLink
    );

    const locationDisplayName = date?.location?.title || date.locationString;

    const hasServiceProvider = !!date?.serviceProvider;

    const renderCalendarAction = () => {
        if (!canAccessData) {
            return (
                <Box
                    sx={{
                        p: 1,
                        display: 'inline-flex',
                        verticalAlign: 'middle',
                        color: 'text.secondary',
                    }}
                >
                    <Icon path={mdiDotsVertical} size="2.3rem" />
                </Box>
            );
        }

        if (hasServiceProvider && date.webinarLink) {
            return (
                <IconButton
                    aria-label={translate('openWebinar')}
                    component={Link}
                    href={date.webinarLink}
                    iconPath={
                        date.isServiceProviderWebinar
                            ? mdiMicrosoftTeams
                            : mdiLaptopAccount
                    }
                    iconSize="2.3rem"
                    sx={{ color: 'text.secondary' }}
                    target="_blank"
                />
            );
        }

        return (
            <CalendarMenu
                date={{
                    startDate: date.startDate,
                    endDate: date.endDate,
                }}
                description={description}
                extraMenuItems={
                    date?.webinarLink && (
                        <MenuItem
                            component={Link}
                            href={date.webinarLink}
                            target="_blank"
                        >
                            {translate('openWebinar')}
                        </MenuItem>
                    )
                }
                iconPath={mdiDotsVertical}
                iconSx={{ color: 'text.secondary' }}
                location={locationDisplayName}
                title={offerTitle}
            />
        );
    };

    const handleListItemClick = () => {
        window.open(date.webinarLink, '_blank');
    };

    const canClickListItem = !!date.webinarLink && canAccessData;

    return (
        <ListItem
            button={canClickListItem}
            disabled={isPast}
            disableHover={!canClickListItem}
            px={0}
            sx={{ opacity: isPast ? 0.5 : 1 }}
            onClick={canClickListItem ? handleListItemClick : undefined}
            {...other}
        >
            <ListItemMedia
                disableHover
                isDate
                color="primary"
                colorVariant="bgColor"
                size="small"
                sx={{
                    position: 'relative',
                    borderWidth: 2,
                    borderStyle: 'solid',
                    borderColor: 'primary.main',
                }}
            >
                {!!date.webinarLink && (
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            position: 'absolute',
                            top: -2,
                            left: -2,
                            p: '4px',
                            backgroundColor: 'common.white',
                            borderTopLeftRadius: 7,
                            borderBottomRightRadius: 10,
                        }}
                    >
                        <Box sx={webinarIconWrapperStyle}>
                            <Icon path={mdiLaptopAccount} size="2.2rem" />
                        </Box>
                    </Box>
                )}

                <ListItemMediaDate
                    color="primary"
                    date={date.startDate}
                    textColorVariant="main"
                />
            </ListItemMedia>

            <ListItemText
                primary={
                    <Box sx={{ lineHeight: 1.2 }}>
                        {getOfferEventTimes(date)}
                    </Box>
                }
                secondary={
                    <Stack
                        component="span"
                        // Trainers and webinarlink should still be clickable when list item disabled
                        sx={{ pointerEvents: 'auto' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {!!locationDisplayName && (
                            <OfferEventDateLocation
                                date={date}
                                disabled={!canAccessData}
                            />
                        )}

                        {!!date.trainers?.length &&
                            date.trainers.map((trainer) => (
                                <Box
                                    component={
                                        canAccessData && showChat
                                            ? Link
                                            : 'span'
                                    }
                                    key={trainer.id}
                                    sx={{ fontSize: '1.6rem' }}
                                    {...(canAccessData &&
                                        showChat && {
                                            params: { id: trainer.id },
                                            to: 'USER_CHAT',
                                        })}
                                >
                                    {trainer.name}
                                </Box>
                            ))}

                        {canClickListItem && (
                            <Link
                                href={date.webinarLink}
                                sx={{
                                    fontSize: '1.6rem',
                                    textDecoration: 'underline',
                                }}
                                target="_blank"
                            >
                                {translate(
                                    'offerEventDateListItem.onlineMeeting'
                                )}
                            </Link>
                        )}
                    </Stack>
                }
            />

            {canAccessData && (
                <Box
                    sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {renderCalendarAction()}
                </Box>
            )}

            {canClickListItem && (
                <ListItemSecondaryAction>
                    <Icon path={mdiChevronRight} size="3rem" />
                </ListItemSecondaryAction>
            )}
        </ListItem>
    );
};
